import { ArrowRight } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const SURVEY_URL = process.env.REACT_APP_SURVEY_URL;

const HERO_IMG = "https://images.pexels.com/photos/5456371/pexels-photo-5456371.png?auto=compress&cs=tinysrgb&w=1400";

const steps = [
  { step: "1", title: "Define what you want", body1: "You complete a structured survey about what you're looking for, how you like to connect, and your preferences.", body2: "This isn't a profile. It's the foundation for matching.", bg: "bg-[#F8F7F5]" },
  { step: "2", title: "Matches start at 80%", body1: "We continuously compare your responses with others in your city.", body2: "You'll only see people who meet a minimum 80% alignment threshold.", bg: "bg-white" },
  { step: "3", title: "Matches are released weekly", body1: "When new matches are found, they're released together on Match Monday.", body2: "Both people are notified at the same time.", bg: "bg-[#F8F7F5]" },
  { step: "4", title: "See your matches before you pay", body1: "You'll see match percentage, shared intent, and compatibility insights.", body2: "Photos and full profiles unlock when you connect.", bg: "bg-white" },
  { step: "5", title: "Connection is intentional", body1: "A connection happens when both people choose to connect.", body2: null, bg: "bg-[#F8F7F5]" },
  { step: "6", title: "Access the network", body1: "HAEVN is free to join.", body2: "Membership gives you access to connect, view full profiles, and stay active in the network.", bg: "bg-white" },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">
      <SEO
        title="How It Works"
        path="/how-it-works"
        description="Complete a survey, get matched at 80%+ compatibility, and connect on Match Monday. HAEVN does the searching for you."
      />
      <Navigation />

      {/* Hero — with image */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60" />
        </div>
        <div className="relative z-10 pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeIn>
              <h1 className="font-headline text-4xl sm:text-5xl text-haevn-navy mb-4">
                Here's exactly how HAEVN works
              </h1>
            </FadeIn>
            <FadeIn delay={100}>
              <p className="text-base md:text-lg text-gray-500 font-body">
                Set it once. We do the rest.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      {/* Steps */}
      {steps.map((item) => (
        <section key={item.step} className={`py-16 px-6 md:px-12 ${item.bg}`}>
          <div className="max-w-4xl mx-auto">
            <FadeIn>
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-haevn-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="font-headline text-haevn-teal">{item.step}</span>
                </div>
                <div>
                  <h2 className="font-headline text-2xl md:text-3xl text-haevn-navy mb-4">
                    {item.title}
                  </h2>
                  <p className="text-base text-gray-600 font-body mb-3">{item.body1}</p>
                  {item.body2 && (
                    <p className="text-base text-gray-600 font-body">{item.body2}</p>
                  )}
                </div>
              </div>
            </FadeIn>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-haevn-navy">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <a
              href={SURVEY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-haevn-gold text-white px-10 py-4 font-subhead text-lg hover:bg-haevn-gold/90 transition-all duration-200 group"
              data-testid="hiw-cta-btn"
            >
              Start Your Survey
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
